import { useCallback, useEffect, useRef, useState } from "react";

/**
 * MediaRecorder wrapper for the SENSE cards.
 *
 * Browsers disagree on output format: Safari produces audio/mp4, Chrome and Firefox
 * audio/webm with opus. Rather than force one, pick the first supported type and let
 * the backend store whatever arrives -- the archive keeps the original bytes, and
 * every browser can play back what it recorded.
 */
const PREFERRED_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return PREFERRED_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

export function AudioRecorder({
  value,
  onChange,
}: {
  value: Blob | null;
  onChange: (blob: Blob | null) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Release the microphone if the screen is left mid-recording.
  useEffect(() => stopTracks, [stopTracks]);

  // The object URL is an external resource: created and revoked in the same effect,
  // so StrictMode's double run and unmounting both release it correctly.
  useEffect(() => {
    if (!value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Ressource im Effekt anlegen und freigeben (siehe oben)
      setPlaybackUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPlaybackUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  async function start() {
    setError(null);
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Dieser Browser kann kein Audio aufnehmen. Du kannst stattdessen etwas notieren.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        onChange(blob.size > 0 ? blob : null);
        stopTracks();
      };

      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setSeconds(0);
      tickRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      setError("Zugriff auf das Mikrofon verweigert.");
      stopTracks();
    }
  }

  function stop() {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <div className="stack">
      {error ? <p className="notice notice--error">{error}</p> : null}

      {playbackUrl && !recording ? (
        <>
          <audio controls src={playbackUrl} style={{ width: "100%" }} />
          <button className="btn btn--quiet" onClick={() => onChange(null)} type="button">
            Aufnahme verwerfen
          </button>
        </>
      ) : recording ? (
        <button className="btn btn--danger btn--lg" onClick={stop} type="button">
          <span
            style={{ width: 12, height: 12, borderRadius: 3, background: "currentcolor" }}
            aria-hidden
          />
          Aufnahme stoppen · {mmss}
        </button>
      ) : (
        <button className="btn btn--quiet btn--lg" onClick={() => void start()} type="button">
          <span
            style={{ width: 12, height: 12, borderRadius: "50%", background: "var(--danger)" }}
            aria-hidden
          />
          Audio aufnehmen
        </button>
      )}
    </div>
  );
}
